import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import DatenschutzCheckbox from '../components/DatenschutzCheckbox';
import Erfolgsmeldung from '../components/Erfolgsmeldung';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatDatum(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function renderDynamicField(feld, value, onChange, error) {
  const base = `form-input${error ? ' error' : ''}`;
  const label = (
    <label className="form-label" style={{ marginBottom: '0.25rem' }}>
      {feld.pflicht ? <span className="required">{feld.label}</span> : feld.label}
      {!feld.pflicht && <span className="form-label-optional"> (optional)</span>}
    </label>
  );

  if (feld.typ === 'select') return (
    <div className="form-group" key={feld.id}>
      {label}
      <select className={base} value={value || ''} onChange={e => onChange(e.target.value)}>
        <option value="">– Bitte wählen –</option>
        {(feld.optionen || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      {error && <span className="form-error">{error}</span>}
    </div>
  );

  if (feld.typ === 'boolean') return (
    <div className="form-group" key={feld.id}>
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)}
          style={{ width: 16, height: 16 }} />
        {feld.label}
        {!feld.pflicht && <span className="form-label-optional">(optional)</span>}
      </label>
      {error && <span className="form-error">{error}</span>}
    </div>
  );

  if (feld.typ === 'textarea') return (
    <div className="form-group" key={feld.id}>
      {label}
      <textarea className={`form-textarea${error ? ' error' : ''}`} rows={3}
        value={value || ''} onChange={e => onChange(e.target.value)} />
      {error && <span className="form-error">{error}</span>}
    </div>
  );

  if (feld.typ === 'date') return (
    <div className="form-group" key={feld.id}>
      {label}
      <input type="date" className={base} value={value || ''} onChange={e => onChange(e.target.value)}
        max={new Date().toISOString().split('T')[0]} />
      {error && <span className="form-error">{error}</span>}
    </div>
  );

  if (feld.typ === 'teilnehmer') return (
    <div key={feld.id}>
      <p className="form-section-title">Teilnehmer (falls abweichend von Kontaktperson)</p>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Vorname Teilnehmer</label>
          <input type="text" className="form-input" value={(value || {}).vorname || ''}
            onChange={e => onChange({ ...(value || {}), vorname: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Nachname Teilnehmer</label>
          <input type="text" className="form-input" value={(value || {}).nachname || ''}
            onChange={e => onChange({ ...(value || {}), nachname: e.target.value })} />
        </div>
      </div>
    </div>
  );

  // Default: text
  return (
    <div className="form-group" key={feld.id}>
      {label}
      <input type="text" className={base} value={value || ''} onChange={e => onChange(e.target.value)} />
      {error && <span className="form-error">{error}</span>}
    </div>
  );
}

export default function AngebotFormular() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [angebot, setAngebot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null); // { auf_warteliste, warteliste_position }
  const [serverError, setServerError] = useState('');

  const [kontakt, setKontakt] = useState({ nachname: '', vorname: '', email: '', telefon: '' });
  const [istMitglied, setIstMitglied] = useState(false);
  const [dynamicValues, setDynamicValues] = useState({});
  const [datenschutz, setDatenschutz] = useState(false);
  const [website, setWebsite] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    api.get(`/api/angebote/${id}`)
      .then(r => setAngebot(r.data))
      .catch(() => setServerError('Angebot konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [id]);

  const setDynamic = (feldId, val) => setDynamicValues(p => ({ ...p, [feldId]: val }));

  const validate = () => {
    const e = {};
    if (!kontakt.nachname.trim()) e.nachname = 'Pflichtfeld';
    if (!kontakt.vorname.trim()) e.vorname = 'Pflichtfeld';
    if (!kontakt.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(kontakt.email)) e.email = 'Gültige E-Mail erforderlich';
    if (!datenschutz) e.datenschutz = 'Datenschutz muss akzeptiert werden';

    // Pflichtfelder aus Baukasten
    const felder = angebot?.formular_felder || [];
    felder.filter(f => f.pflicht && f.aktiv !== false && f.typ !== 'teilnehmer').forEach(f => {
      const v = dynamicValues[f.id];
      if (v === undefined || v === null || v === '' || v === false)
        e[`feld_${f.id}`] = 'Pflichtfeld';
    });

    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (website) return;
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError('');

    // Teilnehmer-Felder aus dynamicValues extrahieren
    const teilnehmerFeld = (angebot?.formular_felder || []).find(f => f.typ === 'teilnehmer');
    const teilnehmerWert = teilnehmerFeld ? (dynamicValues[teilnehmerFeld.id] || {}) : {};

    // Zusatzfelder aufbauen (ohne teilnehmer-Typ)
    const zusatzfelder = {};
    (angebot?.formular_felder || [])
      .filter(f => f.typ !== 'teilnehmer' && dynamicValues[f.id] !== undefined)
      .forEach(f => { zusatzfelder[f.label] = dynamicValues[f.id]; });

    try {
      const r = await api.post(`/api/angebote/${id}/anmelden`, {
        kontakt_nachname: kontakt.nachname,
        kontakt_vorname: kontakt.vorname,
        kontakt_email: kontakt.email,
        kontakt_telefon: kontakt.telefon || undefined,
        teilnehmer_vorname: teilnehmerWert.vorname || undefined,
        teilnehmer_nachname: teilnehmerWert.nachname || undefined,
        ist_mitglied: istMitglied,
        zusatzfelder,
        datenschutz_akzeptiert: true,
        website,
      });
      setSuccess(r.data);
    } catch (err) {
      setServerError(err.response?.data?.error || 'Fehler beim Senden. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="tile-container">
      <div className="tile-grid tile-grid--skeleton">
        <div className="tile tile--skeleton" style={{ height: 200 }} />
      </div>
    </div>
  );

  if (serverError && !angebot) return (
    <div className="form-container">
      <button className="back-btn" onClick={() => navigate('/angebote')}>← Zurück</button>
      <div className="alert alert-error">{serverError}</div>
    </div>
  );

  if (success) return (
    <Erfolgsmeldung
      titel={success.auf_warteliste ? 'Auf der Warteliste!' : 'Anmeldung erfolgreich!'}
      text={success.auf_warteliste
        ? `Das Angebot ist leider ausgebucht. Du bist auf Wartelistenposition ${success.warteliste_position}. Wir melden uns, sobald ein Platz frei wird.`
        : `Vielen Dank für deine Anmeldung zu „${angebot?.titel}". Du erhältst in Kürze weitere Informationen.`}
      zurueckPfad="/angebote"
    />
  );

  const felder = (angebot?.formular_felder || []).filter(f => f.aktiv !== false);
  const hatTeilnehmerFeld = felder.some(f => f.typ === 'teilnehmer');
  const istVoll = angebot?.max_teilnehmer && parseInt(angebot.anmeldungen_count) >= parseInt(angebot.max_teilnehmer);

  return (
    <div className="form-container">
      <button className="back-btn" onClick={() => navigate('/angebote')}>← Zurück</button>

      {/* Angebot-Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        {angebot.bild_pfad && (
          <img src={`${API_URL}/uploads/${angebot.bild_pfad}`} alt={angebot.titel}
            style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 8, marginBottom: '1rem' }} />
        )}
        {angebot.typ_name && (
          <span style={{
            fontSize: '0.75rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
            background: (angebot.typ_farbe || '#0099cc') + '22', color: angebot.typ_farbe || '#0099cc',
            display: 'inline-block', marginBottom: '0.5rem',
          }}>{angebot.typ_icon} {angebot.typ_name}</span>
        )}
        <h2 className="form-title" style={{ marginBottom: '0.25rem' }}>{angebot.titel}</h2>
        {angebot.untertitel && <p style={{ color: '#666', margin: '0 0 0.5rem' }}>{angebot.untertitel}</p>}
        {angebot.beschreibung && <p style={{ color: '#444', fontSize: '0.9rem', lineHeight: 1.5 }}>{angebot.beschreibung}</p>}

        {/* Details */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.875rem' }}>
          {angebot.termine?.length > 0 && (
            <div style={{ fontSize: '0.85rem', color: '#555' }}>
              📅 {angebot.termine.map(t => {
                const von = t.uhrzeit_von?.slice(0,5);
                const bis = t.uhrzeit_bis?.slice(0,5);
                return `${formatDatum(t.datum)}${von ? ` ${von}${bis ? `–${bis}` : ''} Uhr` : ''}`;
              }).join(' • ')}
            </div>
          )}
          {angebot.ort && <div style={{ fontSize: '0.85rem', color: '#555' }}>📍 {angebot.ort}</div>}
          {angebot.kursleiter && <div style={{ fontSize: '0.85rem', color: '#555' }}>👤 {angebot.kursleiter}</div>}
        </div>

        {/* Preis */}
        {(angebot.preis_mitglied || angebot.preis_gast) && (
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {angebot.preis_mitglied && (
              <div style={{ background: '#e8f5e9', padding: '6px 12px', borderRadius: 6, fontSize: '0.9rem' }}>
                <strong>{Number(angebot.preis_mitglied).toFixed(2)} €</strong>
                <span style={{ fontSize: '0.78rem', color: '#555', marginLeft: 4 }}>Mitglieder</span>
              </div>
            )}
            {angebot.preis_gast && (
              <div style={{ background: '#f0f4f8', padding: '6px 12px', borderRadius: 6, fontSize: '0.9rem' }}>
                <strong>{Number(angebot.preis_gast).toFixed(2)} €</strong>
                <span style={{ fontSize: '0.78rem', color: '#555', marginLeft: 4 }}>Gäste</span>
              </div>
            )}
          </div>
        )}

        {angebot.zahlungshinweis && (
          <div style={{ background: '#f8f9fa', padding: '10px 14px', borderRadius: 6, fontSize: '0.85rem', marginTop: '0.75rem', color: '#555', borderLeft: '3px solid #0099cc' }}>
            {angebot.zahlungshinweis}
          </div>
        )}

        {istVoll && !angebot.warteliste_aktiv && (
          <div className="alert alert-error" style={{ marginTop: '1rem' }}>
            Dieses Angebot ist leider ausgebucht.
          </div>
        )}
        {istVoll && angebot.warteliste_aktiv && (
          <div style={{ background: '#fff8e1', padding: '10px 14px', borderRadius: 6, fontSize: '0.85rem', marginTop: '0.75rem', color: '#5d4037', borderLeft: '3px solid #ffc107' }}>
            ⚠️ Das Angebot ist ausgebucht. Du kannst dich auf die Warteliste setzen.
          </div>
        )}
      </div>

      {/* Formular */}
      {(!istVoll || angebot.warteliste_aktiv) && (
        <form onSubmit={handleSubmit} noValidate>
          {/* Honeypot */}
          <div className="honeypot" aria-hidden="true">
            <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex="-1" autoComplete="off" />
          </div>

          {serverError && <div className="alert alert-error">{serverError}</div>}

          <p className="form-section-title">Kontaktperson</p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label required">Nachname</label>
              <input type="text" className={`form-input${errors.nachname ? ' error' : ''}`}
                value={kontakt.nachname} onChange={e => { setKontakt(p => ({...p, nachname: e.target.value})); if(errors.nachname) setErrors(p => ({...p, nachname: ''})); }}
                autoComplete="family-name" />
              {errors.nachname && <span className="form-error">{errors.nachname}</span>}
            </div>
            <div className="form-group">
              <label className="form-label required">Vorname</label>
              <input type="text" className={`form-input${errors.vorname ? ' error' : ''}`}
                value={kontakt.vorname} onChange={e => { setKontakt(p => ({...p, vorname: e.target.value})); if(errors.vorname) setErrors(p => ({...p, vorname: ''})); }}
                autoComplete="given-name" />
              {errors.vorname && <span className="form-error">{errors.vorname}</span>}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label required">E-Mail-Adresse</label>
            <input type="email" className={`form-input${errors.email ? ' error' : ''}`}
              value={kontakt.email} onChange={e => { setKontakt(p => ({...p, email: e.target.value})); if(errors.email) setErrors(p => ({...p, email: ''})); }}
              autoComplete="email" />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label className="form-label">Telefon <span className="form-label-optional">(optional)</span></label>
            <input type="tel" className="form-input" value={kontakt.telefon}
              onChange={e => setKontakt(p => ({...p, telefon: e.target.value}))} autoComplete="tel" />
          </div>

          {/* Mitglied-Toggle wenn kein Feld im Baukasten */}
          {!felder.some(f => f.id === 'ist_mitglied') && (angebot.preis_mitglied || angebot.preis_gast) && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={istMitglied} onChange={e => setIstMitglied(e.target.checked)}
                  style={{ width: 16, height: 16 }} />
                Ich bin Vereinsmitglied (Mitgliedspreis)
              </label>
            </div>
          )}

          {/* Teilnehmer-Sektion */}
          {hatTeilnehmerFeld && (
            <>
              {renderDynamicField(
                felder.find(f => f.typ === 'teilnehmer'),
                dynamicValues['teilnehmer_name'],
                (v) => setDynamic('teilnehmer_name', v),
                null
              )}
            </>
          )}

          {/* Dynamische Felder (außer Teilnehmer) */}
          {felder.filter(f => f.typ !== 'teilnehmer').length > 0 && (
            <>
              <p className="form-section-title">Weitere Angaben</p>
              {felder.filter(f => f.typ !== 'teilnehmer').map(f => {
                // Speziell: ist_mitglied setzt auch den State
                if (f.id === 'ist_mitglied') {
                  return (
                    <div className="form-group" key={f.id}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.9rem' }}>
                        <input type="checkbox" checked={!!dynamicValues[f.id]}
                          onChange={e => { setDynamic(f.id, e.target.checked); setIstMitglied(e.target.checked); }}
                          style={{ width: 16, height: 16 }} />
                        {f.label}
                        {!f.pflicht && <span className="form-label-optional">(optional)</span>}
                      </label>
                    </div>
                  );
                }
                return renderDynamicField(
                  f,
                  dynamicValues[f.id],
                  (v) => { setDynamic(f.id, v); if(errors[`feld_${f.id}`]) setErrors(p => ({...p, [`feld_${f.id}`]: ''})); },
                  errors[`feld_${f.id}`]
                );
              })}
            </>
          )}

          <DatenschutzCheckbox
            checked={datenschutz}
            onChange={e => { setDatenschutz(e.target.checked); if(errors.datenschutz) setErrors(p => ({...p, datenschutz: ''})); }}
            error={errors.datenschutz}
          />

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Wird gesendet…' : (istVoll ? 'Auf Warteliste setzen' : 'Jetzt anmelden')}
          </button>
        </form>
      )}
    </div>
  );
}
